import { useEffect, useMemo, useRef, useCallback } from "react";
import { useSettingsContext } from "src/core/settings/useSettings";

export const useFormDefaults = (config: FormDefaultsConfig) => {
  const { settings, setMultipleSettings, deleteSetting, settingsReady } = useSettingsContext();
  const { formKey, isEditPage, fields } = config;

  const enabled = computeEnabled(settings, formKey);
  const isCreatePage = !isEditPage;

  const normalizedFields = useMemo(() => normalizeFields(fields), [fields]);
  const settingsDefaults = settings?.formDefaults?.[formKey] || {};

  // Persist across renders
  const appliedRef = useRef<Record<string, boolean>>({});
  const programmaticSetRef = useRef<Record<string, number>>({});
  const lastSeededRef = useRef<Record<string, any>>({});
  const autoSetRef = useRef<Record<string, boolean>>({});
  const prevEnabledRef = useRef<boolean>(enabled);

  const setEnabled = useCallback((next: boolean) => { setMultipleSettings({ key: ["formDefaultsEnabledByForm", formKey], value: next }); }, [setMultipleSettings, formKey]);

  const clearStoredDefaults = useCallback(() => {
    deleteSetting(["formDefaults", formKey]);
    appliedRef.current = {};
    lastSeededRef.current = {};
    autoSetRef.current = {};
  }, [deleteSetting, formKey]);

  const clearFields = useCallback((formikProps: any) => {
    const { setFieldValue, setFieldTouched } = formikProps;
    Object.entries(normalizedFields).forEach(([fieldName, cfg]) => {
      const clearValue = cfg.clearValue;
      const nextEmpty = typeof clearValue === "function" ? (clearValue as any)() : clearValue;
      const valueToSet = nextEmpty !== undefined ? nextEmpty : "";
      programmaticSetRef.current[fieldName] = Date.now();
      setFieldValue(fieldName, valueToSet, false);
      setFieldTouched?.(fieldName, false, false);
      autoSetRef.current[fieldName] = false;
      appliedRef.current[fieldName] = true;
    });
  }, [normalizedFields]);

  const FormDefaultsListener = useCallback(({ formikProps }: { formikProps: any }) => {
    const { values, touched, setFieldValue, setFieldTouched } = formikProps;

    /* -------------------- CLEAR AUTO-SET VALUES WHEN DISABLING -------------------- */
    useEffect(() => {
      if (!isCreatePage) return;
      if (!settingsReady) return;
      const prev = prevEnabledRef.current;
      prevEnabledRef.current = enabled;
      if (!(prev === true && enabled === false)) return;
      Object.entries(normalizedFields).forEach(([fieldName, cfg]) => {
        if (!autoSetRef.current[fieldName]) return;
        if (touched?.[fieldName]) return;
        const isEmpty = cfg.isEmpty || defaultIsEmpty;
        const currentValue = values?.[fieldName];
        if (isEmpty(currentValue, values)) return;
        const clearValue = cfg.clearValue;
        const nextEmpty = typeof clearValue === "function" ? (clearValue as any)() : clearValue;
        const valueToSet = nextEmpty !== undefined ? nextEmpty : "";
        programmaticSetRef.current[fieldName] = Date.now();
        setFieldValue(fieldName, valueToSet, false);
        setFieldTouched?.(fieldName, false, false);
        autoSetRef.current[fieldName] = false;
        appliedRef.current[fieldName] = true;
      });
    }, [enabled, isCreatePage, settingsReady, normalizedFields, touched, values, setFieldValue, setFieldTouched]);

    /* -------------------- APPLY DEFAULTS (WAIT FOR FALLBACKS) + SEED SETTINGS -------------------- */
    useEffect(() => {
      if (!isCreatePage) return;
      if (!settingsReady) return;
      if (!enabled) return;
      const patches: any[] = [];
      Object.entries(normalizedFields).forEach(([fieldName, cfg]) => {
        if (appliedRef.current[fieldName]) return;
        const isEmpty = cfg.isEmpty || defaultIsEmpty;
        const currentValue = values?.[fieldName];
        if (!isEmpty(currentValue, values)) {
          appliedRef.current[fieldName] = true;
          return;
        }
        if (touched?.[fieldName]) {
          appliedRef.current[fieldName] = true;
          return;
        }
        const settingKey = cfg.settingKey || fieldName;
        const fromSettings = settingsDefaults?.[settingKey];
        let nextValue = fromSettings;
        let usedFallback = false;
        if (defaultIsEmpty(nextValue)) {
          const fb = cfg.fallback;
          nextValue = typeof fb === "function" ? (fb as any)() : fb;
          usedFallback = !defaultIsEmpty(nextValue);
        }
        if (defaultIsEmpty(nextValue)) return;
        if (cfg.normalize) nextValue = cfg.normalize(nextValue);
        programmaticSetRef.current[fieldName] = Date.now();
        setFieldValue(fieldName, nextValue, false);
        setFieldTouched?.(fieldName, false, false);
        appliedRef.current[fieldName] = true;
        autoSetRef.current[fieldName] = true;
        if (usedFallback && defaultIsEmpty(fromSettings)) {
          const prevSeeded = lastSeededRef.current[fieldName];
          if (!(prevSeeded === nextValue || JSON.stringify(prevSeeded) === JSON.stringify(nextValue))) {
            lastSeededRef.current[fieldName] = nextValue;
            patches.push({ key: ["formDefaults", formKey, settingKey], value: nextValue });
          }
        }
      });
      if (patches.length) setMultipleSettings(patches);
    }, [enabled, isCreatePage, settingsReady, normalizedFields, values, touched, setFieldValue, setFieldTouched, formKey, settingsDefaults, setMultipleSettings]);

    /* -------------------- SAVE MANUAL CHANGES (TOUCHED) -------------------- */
    useEffect(() => {
      if (!isCreatePage) return;
      if (!settingsReady) return;
      if (!enabled) return;
      const patches: any[] = [];
      Object.entries(normalizedFields).forEach(([fieldName, cfg]) => {
        if (!touched?.[fieldName]) return;
        const programmaticAt = programmaticSetRef.current[fieldName];
        if (programmaticAt && Date.now() - programmaticAt < 500) return;
        const settingKey = cfg.settingKey || fieldName;
        let nextValue = values?.[fieldName];
        if (cfg.normalize) nextValue = cfg.normalize(nextValue);
        patches.push({ key: ["formDefaults", formKey, settingKey], value: nextValue });
        autoSetRef.current[fieldName] = false;
      });
      if (patches.length) setMultipleSettings(patches);
    }, [enabled, isCreatePage, settingsReady, normalizedFields, touched, values, formKey, setMultipleSettings]);
    return null;
  }, [enabled, formKey, isCreatePage, normalizedFields, settingsDefaults, settingsReady, setMultipleSettings]);

  const formDefaultsProps: FormDefaultsProps = useMemo(() => ({ enabled, setEnabled, clearStoredDefaults, clearFields, }), [enabled, setEnabled, clearStoredDefaults, clearFields]);

  return { FormDefaultsListener, formDefaultsProps };
};

const defaultIsEmpty = (v: any) => v == null || v === "";

const normalizeFields = (fields: FormDefaultsConfig["fields"]) => {
  const result: Record<string, FieldConfig> = {};
  Object.entries(fields || {}).forEach(([fieldName, cfg]) => {
    if (typeof cfg === "function") {
      result[fieldName] = { fallback: cfg as any };
      return;
    }
    if (cfg && typeof cfg === "object" && !Array.isArray(cfg)) {
      result[fieldName] = cfg as FieldConfig;
      return;
    }
    result[fieldName] = { fallback: cfg };
  });
  return result;
};

const computeEnabled = (settings: AnyObj, formKey: string) => {
  const globalEnabled = settings?.formDefaultsEnabled;
  const byForm = settings?.formDefaultsEnabledByForm?.[formKey];
  const global = typeof globalEnabled === "boolean" ? globalEnabled : true; // default ON
  if (typeof byForm === "boolean") return byForm;
  return global;
};

type AnyObj = Record<string, any>;

type FieldConfig = {
  settingKey?: string;
  fallback?: any | (() => any);
  isEmpty?: (value: any, values: AnyObj) => boolean;
  normalize?: (value: any) => any;
  clearValue?: any | (() => any);
};

export type FormDefaultsConfig = {
  formKey: string;
  isEditPage: boolean;
  fields: Record<string, FieldConfig | any | (() => any)>;
};

export type FormDefaultsProps = {
  enabled: boolean;
  setEnabled: (next: boolean) => void;
  /** deletes saved defaults in settings */
  clearStoredDefaults: () => void;
  /** clears fields in the form (uses field clearValue rules) */
  clearFields: (formikProps: any) => void;
};
